import { useEffect, useRef, useState } from "react";

import { useNavigate } from "@remix-run/react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

import { Button } from "~/components/ui/button";
import { cn } from "~/lib/utils";

import { useTourContext } from "../context/tour-context";
import type { TooltipPosition } from "../utils/positioning";
import {
    calculateTooltipPosition,
    getElementRect,
    resolveTourSelector,
} from "../utils/positioning";

// AIDEV-NOTE: Tour tooltip displays step content and navigation controls
// Auto-positions itself relative to the highlighted element

export function TourTooltip() {
    const {
        isActive,
        currentStep,
        currentStepIndex,
        totalSteps,
        hasNext,
        hasPrevious,
        nextStep,
        previousStep,
        endTour,
    } = useTourContext();

    const navigate = useNavigate();
    const tooltipRef = useRef<HTMLDivElement>(null);
    const [position, setPosition] = useState<TooltipPosition | null>(null);

    // Calculate tooltip position
    useEffect(() => {
        if (!isActive || !currentStep || !tooltipRef.current) {
            setPosition(null);
            return;
        }

        // Find target element - must be visible (offsetParent !== null)
        const selector = resolveTourSelector(currentStep.targetSelector);

        const allMatches = document.querySelectorAll(selector);
        const targetElement = Array.from(allMatches).find(
            (el) => (el as HTMLElement).offsetParent !== null,
        ) as HTMLElement | undefined;

        if (!targetElement) {
            setPosition(null);
            return;
        }

        const updatePosition = () => {
            if (!tooltipRef.current) return;

            const targetRect = getElementRect(targetElement);
            const tooltipRect = tooltipRef.current.getBoundingClientRect();

            // AIDEV-NOTE: Wait for tooltip to have dimensions before positioning
            if (tooltipRect.width === 0 || tooltipRect.height === 0) {
                return;
            }

            const newPosition = calculateTooltipPosition(
                targetRect,
                tooltipRect.width,
                tooltipRect.height,
                currentStep.position || "bottom",
            );

            setPosition(newPosition);
        };

        // AIDEV-NOTE: Use multiple attempts to wait for tooltip to render
        let attempts = 0;
        const maxAttempts = 10;
        const checkAndPosition = () => {
            if (!tooltipRef.current) return;

            const tooltipRect = tooltipRef.current.getBoundingClientRect();
            if (tooltipRect.width === 0 || tooltipRect.height === 0) {
                attempts++;
                if (attempts < maxAttempts) {
                    setTimeout(checkAndPosition, 50);
                }
            } else {
                updatePosition();
            }
        };

        checkAndPosition();

        // Update on scroll/resize
        window.addEventListener("scroll", updatePosition, true);
        window.addEventListener("resize", updatePosition);

        return () => {
            window.removeEventListener("scroll", updatePosition, true);
            window.removeEventListener("resize", updatePosition);
        };
    }, [isActive, currentStep]);

    if (!isActive || !currentStep) {
        return null;
    }

    const isLastStep = currentStepIndex === totalSteps - 1;

    // AIDEV-NOTE: A step's finish button may navigate (e.g. "Visit" → "/help")
    // before ending the tour. SPA navigate, not a synthetic click on the
    // spotlighted link (which was unreliable). endTour first so tour UI clears.
    const handleFinish = () => {
        const href = currentStep.finishHref;
        endTour();
        if (href) {
            navigate(href);
        }
    };

    return (
        <div
            ref={tooltipRef}
            className={cn(
                "fixed z-[200] w-full max-w-md rounded-lg bg-background p-6 shadow-2xl transition-all duration-300",
                "rounded-tl-2xl rounded-br-2xl",
                !position && "opacity-0",
            )}
            style={
                position
                    ? {
                          top: position.top,
                          left: position.left,
                      }
                    : {
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                      }
            }
            onClick={(e) => {
                // Stop click propagation to prevent backdrop from capturing clicks
                e.stopPropagation();
            }}
        >
            {/* Header with progress and close button */}
            <div className="mb-4 flex items-center gap-3">
                {/* Progress indicator */}
                <div className="flex flex-1 items-center gap-2">
                    <div className="flex flex-1 gap-1">
                        {Array.from({ length: totalSteps }).map((_, idx) => (
                            <div
                                key={idx}
                                className={cn(
                                    "h-1 flex-1 rounded-full transition-colors",
                                    idx === currentStepIndex
                                        ? "bg-brand-forest-600"
                                        : idx < currentStepIndex
                                          ? "bg-brand-forest-400"
                                          : "bg-brand-forest-100",
                                )}
                            />
                        ))}
                    </div>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {currentStepIndex + 1} / {totalSteps}
                    </span>
                </div>

                {/* Close button */}
                <button
                    onClick={endTour}
                    className="flex-shrink-0 rounded-sm text-brand-forest-700 opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    aria-label="Close tour"
                >
                    <X className="size-5" strokeWidth={3} />
                </button>
            </div>

            {/* Content */}
            <div className="space-y-3">
                <h3 className="text-lg font-semibold text-brand-forest-700">
                    {currentStep.title}
                </h3>
                <p className="text-sm leading-relaxed text-brand-forest-800">
                    {currentStep.content}
                </p>
                {currentStep.exampleQueries &&
                currentStep.exampleQueries.length > 0 ? (
                    <ul className="list-disc space-y-1 pl-5 text-sm leading-relaxed text-brand-forest-700">
                        {currentStep.exampleQueries.map((query) => (
                            <li key={query}>{query}</li>
                        ))}
                    </ul>
                ) : null}
            </div>

            {/* Navigation buttons */}
            <div className="mt-6 flex items-center justify-between">
                <div className="flex gap-2">
                    {hasPrevious && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={previousStep}
                            className="gap-1 border-brand-forest-200 text-brand-forest-700 hover:bg-brand-forest-50 hover:text-brand-forest-800"
                        >
                            <ChevronLeft className="size-4" />
                            Back
                        </Button>
                    )}
                </div>

                <div className="flex gap-2">
                    {/* AIDEV-NOTE: Tour buttons are forest (subtle + primary), not
                        the default red/terra Button variants. Global to all tours. */}
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={endTour}
                        className="text-brand-forest-700 hover:bg-brand-forest-50 hover:text-brand-forest-800"
                    >
                        Cancel
                    </Button>
                    {hasNext ? (
                        <Button
                            size="sm"
                            variant="solidForest"
                            onClick={nextStep}
                            className="gap-1"
                        >
                            Next
                            <ChevronRight className="size-4" />
                        </Button>
                    ) : (
                        <Button
                            size="sm"
                            variant="solidForest"
                            onClick={handleFinish}
                        >
                            {currentStep.finishButtonText ??
                                (isLastStep ? "Finish" : "Done")}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}
